import type { CaipNetwork } from "@reown/appkit";
import { solana, solanaDevnet, solanaTestnet } from "@reown/appkit/networks";
import {
  arbitrum as arbitrumBase,
  avalanche as avalancheBase,
  base as baseBase,
  bsc as bscBase,
  fantom as fantomBase,
  hoodi as hoodiBase,
  kaia as kaiaBase,
  mainnet as mainnetBase,
  optimism as optimismBase,
  polygon as polygonBase,
} from "viem/chains";

/**
 * Global asset configuration for the widget
 * This defines which assets and networks are supported across the application
 */
export const hoodi: CaipNetwork = {
  ...hoodiBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:560048",
  rpcUrls: {
    default: {
      http: ["/proxy/ethereum/hoodi/native", "https://ethereum-hoodi-rpc.publicnode.com/"],
    },
  },
};

const mainnet: CaipNetwork = {
  ...mainnetBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:1",
  rpcUrls: {
    default: {
      http: ["/proxy/ethereum/mainnet/native", "https://eth.merkle.io/"],
    },
  },
};

const optimism: CaipNetwork = {
  ...optimismBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:10",
  rpcUrls: {
    default: {
      http: ["/proxy/optimism/mainnet/native/http-rpc", "https://mainnet.optimism.io"],
    },
  },
};

const base: CaipNetwork = {
  ...baseBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:8453",
  rpcUrls: {
    default: {
      http: ["/proxy/base/mainnet/native/http-rpc", "https://base-rpc.publicnode.com"],
    },
  },
};

const arbitrum: CaipNetwork = {
  ...arbitrumBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:42161",
  rpcUrls: {
    default: {
      http: ["/proxy/arbitrum/mainnet-one/native/http-rpc", "https://arbitrum.drpc.org"],
    },
  },
};

const kaia: CaipNetwork = {
  ...kaiaBase,
  chainNamespace: "eip155",
  caipNetworkId: `eip155:${kaiaBase.id}`,
  rpcUrls: {
    default: {
      http: ["https://public-en.node.kaia.io", "https://kaia.blockpi.network/v1/rpc/public"],
    },
  },
};

const bsc: CaipNetwork = {
  ...bscBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:56",
  rpcUrls: {
    default: {
      http: ["https://bsc-dataseed.binance.org/", "https://rpc.ankr.com/bsc"],
    },
  },
};

const avalanche: CaipNetwork = {
  ...avalancheBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:43114",
  rpcUrls: {
    default: {
      http: [
        "https://svc.blockdaemon.com/avalanche/mainnet/native/http-rpc/ext/bc/c/eth",
        "https://api.avax.network/ext/bc/C/rpc",
        "https://rpc.ankr.com/avalanche",
      ],
    },
  },
};

const polygon: CaipNetwork = {
  ...polygonBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:137",
  rpcUrls: {
    default: {
      http: [
        "https://svc.blockdaemon.com/polygon/mainnet/native/http-rpc",
        "https://polygon-rpc.com",
        "https://rpc.ankr.com/polygon",
      ],
    },
  },
};

const fantom: CaipNetwork = {
  ...fantomBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:250",
  rpcUrls: {
    default: {
      http: ["https://rpc.fantom.network", "https://rpc.ankr.com/fantom"],
    },
  },
};

// Solana networks
const solanaMainnet: CaipNetwork = {
  ...solana,
  chainNamespace: "solana",
  caipNetworkId: solana.caipNetworkId || "solana:5eykt4UsFv8P8JJd4ESu47Z26wfmJSqeLZyqLdqyHi2K",
  rpcUrls: {
    default: {
      http: ["https://svc.blockdaemon.com/solana/mainnet/native"],
    },
  },
};

const solanaTestnetNetwork: CaipNetwork = {
  ...solanaTestnet,

  chainNamespace: "solana",
  caipNetworkId:
    solanaTestnet.caipNetworkId || "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3zQawwpjk2NsNY",
  rpcUrls: {
    default: {
      http: ["https://svc.blockdaemon.com/solana/testnet/native"],
    },
  },
};

const solanaDevnetNetwork: CaipNetwork = {
  ...solanaDevnet,

  chainNamespace: "solana",
  caipNetworkId:
    solanaDevnet.caipNetworkId || "solana:EtWTRABZaYq6iMfeYKoua166SiEi7Rpe7FXy5Trmz2a6",
  rpcUrls: {
    default: {
      http: ["https://svc.blockdaemon.com/solana/devnet/native"],
    },
  },
};

interface AssetConfig {
  symbol: string;
  name: string;
  networks: Array<CaipNetwork>; // Network configuration objects
}

/**
 * Module-specific asset configurations
 * Each array explicitly defines which assets and networks that module supports
 */

// DeFi module: Supports ETH on specific networks (can be filtered further by config)
export const DEFI_ASSETS: AssetConfig[] = [
  {
    symbol: "eth",
    name: "Ethereum",
    networks: [hoodi, arbitrum, base, mainnet, optimism],
  },
];

// Staking module: Supports ETH on specific networks
export const STAKING_ASSETS: AssetConfig[] = [
  {
    symbol: "eth",
    name: "Ethereum",
    networks: [hoodi, mainnet],
  },
  {
    symbol: "sol",
    name: "Solana",
    networks: [solanaMainnet, solanaTestnetNetwork, solanaDevnetNetwork],
  },
];

// Swap module: Supports ETH on specific networks
export const SWAP_ASSETS: AssetConfig[] = [
  {
    symbol: "eth",
    name: "Ethereum",
    networks: [hoodi, mainnet, arbitrum, base, optimism, bsc, avalanche, polygon, fantom, kaia],
  },
];

// Portfolio module: Currently no restrictions (empty array = all networks allowed)
export const PORTFOLIO_ASSETS: AssetConfig[] = [];

/**
 * Get all unique assets across DeFi, Staking, and Swap modules
 * Aggregates networks for each unique asset symbol
 * Used for wallet initialization
 */
function getAllAssets(): AssetConfig[] {
  const allAssets = [...DEFI_ASSETS, ...STAKING_ASSETS, ...SWAP_ASSETS];
  const assetMap = new Map<
    string,
    { symbol: string; name: string; networks: Map<string | number, CaipNetwork> }
  >();

  // Collect all unique assets and their networks
  for (const asset of allAssets) {
    const symbolKey = asset.symbol.toLowerCase();

    let assetEntry = assetMap.get(symbolKey);
    if (!assetEntry) {
      assetEntry = {
        symbol: asset.symbol,
        name: asset.name,
        networks: new Map<string | number, CaipNetwork>(),
      };
      assetMap.set(symbolKey, assetEntry);
    }

    // Add all networks for this asset
    for (const network of asset.networks) {
      assetEntry.networks.set(network.caipNetworkId, network);
    }
  }

  // Convert to AssetConfig array
  // Ensure network id is always a string (viem chains may have numeric ids)
  return Array.from(assetMap.values()).map((entry) => ({
    symbol: entry.symbol,
    name: entry.name,
    networks: Array.from(entry.networks.values()),
  }));
}

/**
 * Get network configurations from assets
 */
export function getSupportedNetworks(): CaipNetwork[] {
  return getAllAssets().flatMap((asset) => asset.networks);
}

/**
 * Get all supported asset symbols in lowercase
 * @param assets - Optional array of assets to get symbols from (defaults to all assets)
 */
export function getSupportedAssetSymbols(assets?: AssetConfig[]): string[] {
  const assetsToUse = assets || getAllAssets();
  return assetsToUse.map((asset) => asset.symbol);
}

/**
 * Check if an asset symbol is supported
 * @param symbol - Asset symbol to check
 * @param assets - Optional array of assets to check against (defaults to all assets)
 */
export function isAssetSupported(symbol: string, assets?: AssetConfig[]): boolean {
  const normalizedSymbol = symbol.toLowerCase();
  return getSupportedAssetSymbols(assets).includes(normalizedSymbol);
}
